import React, { useEffect, useState } from 'react';
import { FileText, BookOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const DocumentsPage: React.FC = () => {
  const [documents, setDocuments] = useState<any[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const docs = JSON.parse(localStorage.getItem('ecstatic_uploaded_documents') || '[]');
    setDocuments(docs);
  }, []);

  const handleStudyDocument = (document: any) => {
    navigate('/study', { state: { studyContent: [document] } });
  };

  return (
    <div className="h-full space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Documents</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">Manage your uploaded documents</p>
      </div>
      
      {documents.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <FileText className="h-8 w-8 mx-auto mb-3 text-gray-400" />
          <p className="text-sm">No documents uploaded yet.</p>
          <p className="text-xs mt-1">Upload your first document to get started!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {documents.map((doc, idx) => (
            <div key={idx} className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow">
              <div className="flex items-start space-x-3">
                <div className="p-2 bg-primary-100 dark:bg-primary-900/50 rounded-lg">
                  <FileText className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-gray-900 dark:text-white truncate text-sm">{doc.fileName}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{formatFileSize(doc.fileSize)}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">Uploaded: {doc.processedAt ? new Date(doc.processedAt).toLocaleDateString() : ''}</p>
                  
                  {/* Study Content Stats - Compact */}
                  {doc.studyContent && (
                    <div className="mt-2 flex items-center space-x-3 text-xs">
                      <div className="flex items-center space-x-1">
                        <BookOpen className="h-3 w-3 text-primary-600" />
                        <span>{doc.studyContent.flashcards?.length || 0} cards</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <span className="text-secondary-600">●</span>
                        <span>{doc.studyContent.questions?.length || 0} questions</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <span className="text-success-600">✓</span>
                        <span>Ready</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Study Button - Compact */}
                  {doc.studyContent && (
                    <button
                      onClick={() => handleStudyDocument(doc)}
                      className="mt-3 w-full bg-primary-600 text-white px-3 py-2 rounded-lg text-xs font-medium hover:bg-primary-700 transition-colors flex items-center justify-center space-x-1"
                    >
                      <BookOpen className="h-3 w-3" />
                      <span>Study</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}; 